import React from "react";

function CodeBlock({ text, label = "Copy" }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };
  return (
    <div className="stt-code">
      <pre className="stt-pre" aria-label="code snippet">
        <code>{text}</code>
      </pre>
      <button className="stt-btn stt-btn-outline" onClick={copy}>
        {copied ? "Copied!" : label}
      </button>
    </div>
  );
}

export default function SoftauthDocs() {
  const installText = `pip install softauth`;

  const fastapiText = `from fastapi import FastAPI, Depends
from softauth.fastapi import AuthManager, require_roles

app = FastAPI()
auth = AuthManager(secret="your-secret-key", algorithm="HS256")

@app.post("/login")
def login(username: str, password: str):
    # verify credentials however you like
    token = auth.create_token({"sub": username, "roles": ["admin"]})
    return {"access_token": token}

@app.get("/protected")
def protected(user=Depends(auth.current_user)):
    return {"hello": user["sub"]}

@app.get("/admin-only")
@require_roles("admin")
def admin_only(user=Depends(auth.current_user)):
    return {"admin": True}`;

  const flaskText = `from flask import Flask, request, jsonify
from softauth.flask import AuthManager, login_required, role_required

app = Flask(__name__)
auth = AuthManager(app, secret="your-secret-key")

@app.post("/login")
def login():
    token = auth.create_token({"sub": "user1", "roles": ["editor"]})
    return jsonify(access_token=token)

@app.get("/protected")
@login_required
def protected():
    return jsonify(user=request.current_user)

@app.get("/editor-only")
@role_required("editor")
def editor_only():
    return jsonify(ok=True)`;

  const envText = `SECRET_KEY=change_me_in_production
ACCESS_TOKEN_EXPIRE_MINUTES=60
ALGORITHM=HS256`;

  return (
    <main className="docs-wrap">
      <div className="docs-layout">
        <aside className="docs-toc">
          <strong>On this page</strong>
          <a href="#intro">Introduction</a>
          <a href="#install">Install</a>
          <a href="#fastapi">FastAPI Usage</a>
          <a href="#flask">Flask Usage</a>
          <a href="#roles">Role Guards</a>
          <a href="#config">Configuration</a>
        </aside>

        <article>
          <header className="stt-hero">
            <div className="stt-pill">softauth • Docs</div>
            <h1 className="stt-h1">Zero-setup JWT auth for FastAPI & Flask</h1>
            <p className="stt-lead">
              Plug-in decorators, token-lifecycle management, and role-based guards — out of the box.
            </p>
          </header>

          <section id="intro" className="stt-section">
            <h2 className="stt-h2">What is softauth?</h2>
            <p className="stt-p">
              softauth gives you <strong>JWT authentication in minutes</strong> for FastAPI or Flask apps.
              Drop in an <code>AuthManager</code>, wrap routes with a decorator, and you're done — no
              boilerplate token-parsing code, no manual role checks.
            </p>
          </section>

          <section id="install" className="stt-section stt-card">
            <h2 className="stt-card-title">Install</h2>
            <div className="stt-card-body">
              <CodeBlock text={installText} label="Copy" />
            </div>
          </section>

          <section id="fastapi" className="stt-section stt-card">
            <h2 className="stt-card-title">FastAPI Usage</h2>
            <div className="stt-card-body">
              <CodeBlock text={fastapiText} label="Copy snippet" />
            </div>
          </section>

          <section id="flask" className="stt-section stt-card">
            <h2 className="stt-card-title">Flask Usage</h2>
            <div className="stt-card-body">
              <CodeBlock text={flaskText} label="Copy snippet" />
            </div>
          </section>

          <section id="roles" className="stt-section stt-grid-2">
            <article className="stt-card">
              <h2 className="stt-card-title">Role Guards</h2>
              <div className="stt-card-body">
                <ul className="stt-list">
                  <li>Roles are embedded in the JWT payload under <code>"roles"</code>.</li>
                  <li>Use <code>@require_roles("admin")</code> (FastAPI) or <code>@role_required("admin")</code> (Flask).</li>
                  <li>Multiple roles: <code>require_roles("admin", "editor")</code> — any match passes.</li>
                </ul>
              </div>
            </article>
            <article className="stt-card">
              <h2 className="stt-card-title">Token Lifecycle</h2>
              <div className="stt-card-body">
                <ul className="stt-list">
                  <li><code>create_token(payload)</code> — signs and returns a bearer token.</li>
                  <li><code>decode_token(token)</code> — verifies signature and expiry.</li>
                  <li>Expiry controlled via <code>ACCESS_TOKEN_EXPIRE_MINUTES</code>.</li>
                </ul>
              </div>
            </article>
          </section>

          <section id="config" className="stt-section stt-card">
            <h2 className="stt-card-title">Configuration</h2>
            <div className="stt-card-body">
              <p className="stt-p">Set via environment variables or pass directly to <code>AuthManager</code>.</p>
              <CodeBlock text={envText} label="Copy .env" />
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}
